import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Check, Loader2, ChevronDown, Search } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Country codes with flags
const COUNTRY_CODES = [
  { code: '+20', country: 'Egypt', flag: '🇪🇬' },
  { code: '+971', country: 'UAE', flag: '🇦🇪' },
  { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦' },
  { code: '+974', country: 'Qatar', flag: '🇶🇦' },
  { code: '+973', country: 'Bahrain', flag: '🇧🇭' },
  { code: '+965', country: 'Kuwait', flag: '🇰🇼' },
  { code: '+968', country: 'Oman', flag: '🇴🇲' },
  { code: '+962', country: 'Jordan', flag: '🇯🇴' },
  { code: '+961', country: 'Lebanon', flag: '🇱🇧' },
  { code: '+44', country: 'United Kingdom', flag: '🇬🇧' },
  { code: '+1', country: 'United States', flag: '🇺🇸' },
  { code: '+1', country: 'Canada', flag: '🇨🇦' },
  { code: '+49', country: 'Germany', flag: '🇩🇪' },
  { code: '+33', country: 'France', flag: '🇫🇷' },
  { code: '+39', country: 'Italy', flag: '🇮🇹' },
  { code: '+34', country: 'Spain', flag: '🇪🇸' },
  { code: '+31', country: 'Netherlands', flag: '🇳🇱' },
  { code: '+41', country: 'Switzerland', flag: '🇨🇭' },
  { code: '+43', country: 'Austria', flag: '🇦🇹' },
  { code: '+32', country: 'Belgium', flag: '🇧🇪' },
  { code: '+46', country: 'Sweden', flag: '🇸🇪' },
  { code: '+47', country: 'Norway', flag: '🇳🇴' },
  { code: '+45', country: 'Denmark', flag: '🇩🇰' },
  { code: '+7', country: 'Russia', flag: '🇷🇺' },
  { code: '+86', country: 'China', flag: '🇨🇳' },
  { code: '+91', country: 'India', flag: '🇮🇳' },
  { code: '+81', country: 'Japan', flag: '🇯🇵' },
  { code: '+82', country: 'South Korea', flag: '🇰🇷' },
  { code: '+65', country: 'Singapore', flag: '🇸🇬' },
  { code: '+60', country: 'Malaysia', flag: '🇲🇾' },
  { code: '+61', country: 'Australia', flag: '🇦🇺' },
  { code: '+64', country: 'New Zealand', flag: '🇳🇿' },
  { code: '+27', country: 'South Africa', flag: '🇿🇦' },
  { code: '+234', country: 'Nigeria', flag: '🇳🇬' },
  { code: '+254', country: 'Kenya', flag: '🇰🇪' },
  { code: '+55', country: 'Brazil', flag: '🇧🇷' },
  { code: '+52', country: 'Mexico', flag: '🇲🇽' },
  { code: '+54', country: 'Argentina', flag: '🇦🇷' },
  { code: '+90', country: 'Turkey', flag: '🇹🇷' },
  { code: '+30', country: 'Greece', flag: '🇬🇷' },
  { code: '+48', country: 'Poland', flag: '🇵🇱' },
  { code: '+420', country: 'Czech Republic', flag: '🇨🇿' },
  { code: '+36', country: 'Hungary', flag: '🇭🇺' },
  { code: '+40', country: 'Romania', flag: '🇷🇴' },
  { code: '+380', country: 'Ukraine', flag: '🇺🇦' },
  { code: '+212', country: 'Morocco', flag: '🇲🇦' },
  { code: '+216', country: 'Tunisia', flag: '🇹🇳' },
  { code: '+213', country: 'Algeria', flag: '🇩🇿' },
  { code: '+218', country: 'Libya', flag: '🇱🇾' },
  { code: '+249', country: 'Sudan', flag: '🇸🇩' },
  { code: '+964', country: 'Iraq', flag: '🇮🇶' },
  { code: '+98', country: 'Iran', flag: '🇮🇷' },
  { code: '+92', country: 'Pakistan', flag: '🇵🇰' },
  { code: '+880', country: 'Bangladesh', flag: '🇧🇩' },
  { code: '+63', country: 'Philippines', flag: '🇵🇭' },
  { code: '+84', country: 'Vietnam', flag: '🇻🇳' },
  { code: '+66', country: 'Thailand', flag: '🇹🇭' },
  { code: '+62', country: 'Indonesia', flag: '🇮🇩' },
];

// Project options for the dropdown
const PROJECT_OPTIONS = [
  { value: 'NEWGIZA', label: 'NEWGIZA' },
  { value: 'NEWKAIRO', label: 'NEWKAIRO' },
  { value: 'Seashell', label: 'Seashell' },
  { value: 'Seashell Ras El Hekma', label: 'Seashell Ras El Hekma' },
  { value: 'Playa Ras El Hekma', label: 'Playa Ras El Hekma' },
  { value: 'Playa Ghazala', label: 'Playa Ghazala' },
  { value: 'IVY', label: 'IVY' },
  { value: 'Artlife', label: 'Artlife' },
  { value: 'Not sure / Need guidance', label: 'Not sure / Need guidance' }
];

// Map URL paths to project values
const PATH_TO_PROJECT = {
  '/seashell': 'Seashell',
  '/projects/seashell': 'Seashell',
  '/projects/seashell-ras-el-hekma': 'Seashell Ras El Hekma',
  '/ivy': 'IVY',
  '/projects/ivy': 'IVY',
  '/projects/newgiza': 'NEWGIZA',
  '/projects/newkairo': 'NEWKAIRO',
  '/projects/playa-ghazala': 'Playa Ghazala',
  '/projects/playa-ras-el-hekma': 'Playa Ras El Hekma'
};

// Country Code Selector Component
const CountryCodeSelector = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Find the selected country
  const selectedCountry = COUNTRY_CODES.find(c => c.code === value) || COUNTRY_CODES[0];

  // Filter countries based on search
  const filteredCountries = COUNTRY_CODES.filter(country => 
    country.country.toLowerCase().includes(searchQuery.toLowerCase()) ||
    country.code.includes(searchQuery)
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (country) => {
    onChange(country.code);
    setIsOpen(false);
    setSearchQuery('');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Selector Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-3 py-3.5 bg-gray-50 border border-gray-200 border-r-0 text-gray-900 hover:bg-gray-100 transition-all rounded-l-none focus:outline-none focus:bg-white focus:border-gray-400"
        style={{ minWidth: '95px' }}
      >
        <span className="text-lg">{selectedCountry.flag}</span>
        <span className="text-sm font-medium">{selectedCountry.code}</span>
        <ChevronDown size={14} className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 shadow-xl z-50 max-h-72 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search country..."
                className="w-full pl-8 pr-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded focus:outline-none focus:border-gray-400 focus:bg-white"
              />
            </div>
          </div>

          {/* Country List */}
          <div className="max-h-52 overflow-y-auto">
            {filteredCountries.length > 0 ? (
              filteredCountries.map((country, index) => (
                <button
                  key={`${country.code}-${country.country}-${index}`}
                  type="button"
                  onClick={() => handleSelect(country)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-50 transition-colors ${
                    country.code === value ? 'bg-blue-50' : ''
                  }`}
                >
                  <span className="text-lg">{country.flag}</span>
                  <span className="text-sm text-gray-900 flex-1">{country.country}</span>
                  <span className="text-sm text-gray-500">{country.code}</span>
                </button>
              ))
            ) : (
              <div className="px-3 py-4 text-sm text-gray-500 text-center">
                No countries found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Phone Number Input Component
const PhoneNumberInput = ({ countryCode, phoneNumber, onCountryCodeChange, onPhoneNumberChange }) => {
  // Format phone number with spaces (basic formatting)
  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    
    // Apply basic formatting (groups of 3-4 digits)
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
    if (digits.length <= 10) return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 10)} ${digits.slice(10, 14)}`;
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    // Only allow digits and spaces
    const cleaned = value.replace(/[^\d\s]/g, '');
    const formatted = formatPhoneNumber(cleaned);
    onPhoneNumberChange(formatted);
  };

  return (
    <div className="flex">
      {/* Country Code Selector */}
      <CountryCodeSelector 
        value={countryCode} 
        onChange={onCountryCodeChange} 
      />
      
      {/* Phone Number Input */}
      <input
        type="tel"
        value={phoneNumber}
        onChange={handlePhoneChange}
        placeholder="100 200 3000"
        className="flex-1 px-4 py-3.5 bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:bg-white focus:border-gray-400 focus:ring-0 outline-none transition-all"
        style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
      />
    </div>
  );
};

export const LeadModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [sourcePage, setSourcePage] = useState('');
  
  const [formData, setFormData] = useState({
    full_name: '',
    country_code: '+20', // Default to Egypt
    phone_number: '',
    email: '',
    project_of_interest: '',
    job_title: '',
    company: '',
    notes: ''
  });

  // Determine if required fields are filled
  const isFormValid = formData.full_name && formData.country_code && formData.phone_number && formData.email && formData.project_of_interest;

  // Get default project based on current path
  const getDefaultProject = useCallback(() => {
    const path = window.location.pathname;
    return PATH_TO_PROJECT[path] || '';
  }, []);

  // Handle opening the modal
  const handleOpen = useCallback(() => {
    const defaultProject = getDefaultProject();
    setFormData(prev => ({
      ...prev,
      project_of_interest: defaultProject
    }));
    setSourcePage(window.location.pathname);
    setIsOpen(true);
    setIsSuccess(false);
    document.body.style.overflow = 'hidden';
  }, [getDefaultProject]);

  // Handle closing the modal
  const handleClose = useCallback(() => {
    setIsOpen(false);
    document.body.style.overflow = 'auto';
    // Reset form after close animation
    setTimeout(() => {
      setFormData({
        full_name: '',
        country_code: '+20',
        phone_number: '',
        email: '',
        project_of_interest: '',
        job_title: '',
        company: '',
        notes: ''
      });
      setIsSuccess(false);
    }, 300);
  }, []);

  // Listen for openLeadModal events
  useEffect(() => {
    const handleOpenEvent = () => handleOpen();
    window.addEventListener('openLeadModal', handleOpenEvent);
    return () => window.removeEventListener('openLeadModal', handleOpenEvent);
  }, [handleOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleClose]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid || isSubmitting) return;

    setIsSubmitting(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          full_name: formData.full_name,
          country_code: formData.country_code,
          phone_number: formData.phone_number,
          email: formData.email,
          project_of_interest: formData.project_of_interest,
          job_title: formData.job_title || '',
          company: formData.company || '',
          notes: formData.notes || '',
          source_page: sourcePage,
          form_type: 'Book a Call'
        }),
      });

      if (response.ok) {
        setIsSuccess(true);
        // Auto close after 3 seconds
        setTimeout(() => {
          handleClose();
        }, 3000);
      } else {
        console.error('Failed to submit lead');
        // Still show success for UX (data could be retried)
        setIsSuccess(true);
        setTimeout(() => {
          handleClose();
        }, 3000);
      }
    } catch (error) {
      console.error('Error submitting lead:', error);
      // Show success anyway for UX purposes
      setIsSuccess(true);
      setTimeout(() => {
        handleClose();
      }, 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-gray-900/70 backdrop-blur-sm transition-opacity"
        onClick={handleClose}
      />
      
      {/* Modal Container */}
      <div className="relative bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl animate-fade-in">
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-5 right-5 w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all z-10"
          aria-label="Close"
        >
          <X size={22} />
        </button>

        {!isSuccess ? (
          /* Form View */
          <div className="p-8 sm:p-10">
            {/* Header */}
            <div className="mb-8">
              <h2 className="text-2xl sm:text-3xl font-light text-gray-900 mb-2">
                Book a Call
              </h2>
              <p className="text-gray-500 text-sm">
                Share your details and our team will reach out shortly.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Required Fields */}
              
              {/* Full Name */}
              <div>
                <label htmlFor="full_name" className="block text-xs font-medium uppercase tracking-wider text-gray-500 mb-2">
                  Full Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  id="full_name"
                  name="full_name"
                  required
                  value={formData.full_name}
                  onChange={handleChange}
                  className="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:bg-white focus:border-gray-400 focus:ring-0 outline-none transition-all"
                  placeholder="Your full name"
                />
              </div>

              {/* Phone Number - Enhanced */}
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider text-gray-500 mb-2">
                  Phone Number <span className="text-red-400">*</span>
                </label>
                <PhoneNumberInput
                  countryCode={formData.country_code}
                  phoneNumber={formData.phone_number}
                  onCountryCodeChange={(code) => setFormData(prev => ({ ...prev, country_code: code }))}
                  onPhoneNumberChange={(number) => setFormData(prev => ({ ...prev, phone_number: number }))}
                />
              </div>

              {/* Email Address */}
              <div>
                <label htmlFor="email" className="block text-xs font-medium uppercase tracking-wider text-gray-500 mb-2">
                  Email Address <span className="text-red-400">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:bg-white focus:border-gray-400 focus:ring-0 outline-none transition-all"
                  placeholder="your@email.com"
                />
              </div>

              {/* Project of Interest */}
              <div>
                <label htmlFor="project_of_interest" className="block text-xs font-medium uppercase tracking-wider text-gray-500 mb-2">
                  Project of Interest <span className="text-red-400">*</span>
                </label>
                <select
                  id="project_of_interest"
                  name="project_of_interest"
                  required
                  value={formData.project_of_interest}
                  onChange={handleChange}
                  className="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 text-gray-900 focus:bg-white focus:border-gray-400 focus:ring-0 outline-none transition-all appearance-none cursor-pointer"
                  style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.75rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.5em 1.5em', paddingRight: '2.5rem' }}
                >
                  <option value="" disabled>Select a project</option>
                  {PROJECT_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-100 pt-5 mt-6">
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-4">Optional Information</p>
              </div>

              {/* Job Title */}
              <div>
                <label htmlFor="job_title" className="block text-xs font-medium uppercase tracking-wider text-gray-500 mb-2">
                  Job Title
                </label>
                <input
                  type="text"
                  id="job_title"
                  name="job_title"
                  value={formData.job_title}
                  onChange={handleChange}
                  className="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:bg-white focus:border-gray-400 focus:ring-0 outline-none transition-all"
                  placeholder="Your position"
                />
              </div>

              {/* Company */}
              <div>
                <label htmlFor="company" className="block text-xs font-medium uppercase tracking-wider text-gray-500 mb-2">
                  Company
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:bg-white focus:border-gray-400 focus:ring-0 outline-none transition-all"
                  placeholder="Company name"
                />
              </div>

              {/* Notes */}
              <div>
                <label htmlFor="notes" className="block text-xs font-medium uppercase tracking-wider text-gray-500 mb-2">
                  Notes
                </label>
                <textarea
                  id="notes"
                  name="notes"
                  rows={3}
                  value={formData.notes}
                  onChange={handleChange}
                  className="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:bg-white focus:border-gray-400 focus:ring-0 outline-none transition-all resize-none"
                  placeholder="Any specific requirements or questions?"
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={!isFormValid || isSubmitting}
                className={`w-full py-4 mt-3 font-medium tracking-wide transition-all duration-300 flex items-center justify-center gap-2 ${
                  isFormValid && !isSubmitting
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 shadow-lg shadow-purple-600/25 cursor-pointer'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Book a Call'
                )}
              </button>

              {/* Privacy Notice */}
              <p className="text-xs text-gray-400 text-center mt-4">
                Your information will only be used to contact you regarding your inquiry.
              </p>
            </form>
          </div>
        ) : (
          /* Success View */
          <div className="p-8 sm:p-10 text-center py-16">
            <div className="w-20 h-20 bg-gradient-to-br from-emerald-50 to-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check size={40} className="text-emerald-600" strokeWidth={2} />
            </div>
            <h3 className="text-2xl font-light text-gray-900 mb-3">
              Thank You
            </h3>
            <p className="text-gray-500 max-w-xs mx-auto">
              A member of our team will contact you shortly.
            </p>
          </div>
        )}
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.95) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-fade-in {
          animation: fadeIn 0.2s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default LeadModal;
